import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/cn';
import { Card } from '../ui';

export default function VideoDisplay({
  title,
  subtitle,
  url,
  description,
  className,
}) {
  // Convert YouTube watch URLs to embed URLs
  const getEmbedUrl = (videoUrl) => {
    if (!videoUrl) return null;

    // YouTube watch URL
    const youtubeMatch = videoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    if (youtubeMatch) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    }

    // Vimeo URL
    const vimeoMatch = videoUrl.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }

    // Return as-is if already an embed URL or other format
    return videoUrl;
  };

  const { t } = useTranslation();
  const embedUrl = getEmbedUrl(url);

  return (
    <Card
      gradient
      elevated
      className={cn('overflow-hidden', className)}
      padding="none"
    >
      <div className="aspect-video bg-gray-900 relative">
        {embedUrl ? (
          <iframe
            src={embedUrl}
            className="absolute inset-0 w-full h-full"
            allowFullScreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            title={title || t('stimulus.videoContent')}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-center text-white">
            <div>
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-white/10 flex items-center justify-center">
                <svg
                  className="w-10 h-10"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
              <p className="text-gray-400 dark:text-gray-500">{t('stimulus.videoUnavailable')}</p>
            </div>
          </div>
        )}
      </div>
      <div className="p-8 text-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {title}
        </h1>
        {subtitle && (
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-4">{subtitle}</p>
        )}
        {description && (
          <p className="text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">{description}</p>
        )}
      </div>
    </Card>
  );
}
